import { ErrorModal, HelmetDexteel, useHasProfile } from "@dexteel/mesf-core";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Group, Panel } from "react-resizable-panels";

import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { Disposition } from "@/pages/mill/schedule/components/mechanical-results/types";
import { invalidateHeatQueries } from "@/pages/mill/schedule/components/mechanical-results/useSaveMechanicalRun";
import { CampaignResponse } from "@/pages/mill/schedule/models/CampaignReponse";
import { upsertMechanicalTestRunDisposition } from "@/pages/mill/schedule/repositories/CampaignRepository";
import { MILL_1, MILL_2, MillId } from "@/setup/mill-config/mill-config";
import { pageBgColor } from "@/utils/theme";
import { HeatDetailsPanel } from "./components/HeatDetailsPanel";
import { MillHeatsTable } from "./components/MillHeatsTable";
import {
	getLastHeatsByMill,
	MillHeatRow,
} from "./repositories/LabDashboardRepository";

const useMillHeats = ({ millId }: { millId: MillId }) =>
	useQuery<MillHeatRow[], Error>({
		queryKey: ["chemistries-mill-heats", millId],
		queryFn: ({ signal }) => getLastHeatsByMill(millId, signal),
		refetchInterval: 60_000,
	});

export const LabDashboardPage = () => {
	const queryClient = useQueryClient();
	const hasProfile = useHasProfile();
	const canEditLabResults =
		hasProfile("Lab Profile") || hasProfile("admin Profile");

	const [error, setError] = useState("");
	const [selection, setSelection] = useState<{
		heat: CampaignResponse;
		millId: MillId;
		editingDisposition: boolean;
	} | null>(null);

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("chemistries-by-mill-panel-sizes");

	const {
		data: mill1Heats,
		isLoading: isLoadingMill1,
		error: errorM1,
		isError: isErrorM1,
	} = useMillHeats({ millId: MILL_1.id });

	const {
		data: mill2Heats,
		isLoading: isLoadingMill2,
		error: errorM2,
		isError: isErrorM2,
	} = useMillHeats({ millId: MILL_2.id });

	const useUpsertMechanicalTestRunDisposition = (millId: MillId) => {
		const mutation = useMutation({
			mutationFn: upsertMechanicalTestRunDisposition,
			onSuccess: (_result, variables) =>
				invalidateHeatQueries(queryClient, {
					heatId: variables.heatId,
					millId,
				}),
			onError: (error: Error) => setError(error.message),
		});

		return mutation;
	};

	const millGrid = (millId: MillId) => {
		const isMill1 = millId === MILL_1.id;
		const disposition = useUpsertMechanicalTestRunDisposition(
			isMill1 ? MILL_1.id : MILL_2.id,
		);

		return (
			<Stack spacing={0.5} sx={{ height: "100%", minHeight: 0, minWidth: 0 }}>
				<Typography variant="subtitle1" fontWeight={600}>
					{isMill1 ? MILL_1.name : MILL_2.name}
				</Typography>
				<Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
					<MillHeatsTable
						rows={(isMill1 ? mill1Heats : mill2Heats) ?? []}
						isLoading={isMill1 ? isLoadingMill1 : isLoadingMill2}
						selectedCampaignId={
							selection?.millId === millId ? selection.heat.CampaignId : null
						}
						isEditingDisposition={
							selection?.millId === millId && selection.editingDisposition
						}
						savingHeatId={
							disposition.isPending ? disposition.variables?.heatId : null
						}
						onSelectHeat={(heat, editingDisposition) =>
							setSelection({ heat, millId, editingDisposition })
						}
						onChangeDisposition={
							canEditLabResults
								? (heatId: number, value: Disposition) =>
										disposition.mutate({ heatId, value })
								: undefined
						}
					/>
				</Box>
			</Stack>
		);
	};

	useEffect(() => {
		if (isErrorM1) {
			setError(errorM1.message);
		}
		if (isErrorM2) {
			setError(errorM2.message);
		}
	}, [isErrorM1, errorM1, isErrorM2, errorM2]);

	return (
		<>
			<HelmetDexteel title="Lab Dashboard" />
			<Stack
				spacing={1}
				sx={{ backgroundColor: pageBgColor, px: 2, pt: 2, pb: 1 }}
			>
				<Typography variant="h5" fontWeight={600}>
					Lab Dashboard
				</Typography>

				<Box sx={{ height: "calc(100vh - 140px)" }}>
					<Group
						orientation="vertical"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={25}>
							<Stack direction="row" spacing={2} sx={{ height: "100%" }}>
								<Box sx={{ flex: 1, minWidth: 0 }}>{millGrid(MILL_1.id)}</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>{millGrid(MILL_2.id)}</Box>
							</Stack>
						</Panel>

						<CustomSeparator
							onDoubleClick={handleSeparatorDoubleClick}
							orientation="vertical"
						/>

						<Panel id="details" minSize={25}>
							<Paper elevation={2} sx={{ height: "100%", width: "100%" }}>
								<HeatDetailsPanel
									heat={selection?.heat ?? null}
									millId={selection?.millId ?? null}
									canEdit={canEditLabResults}
								/>
							</Paper>
						</Panel>
					</Group>
				</Box>
			</Stack>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
